/**
 * 總覽頁面 - 工業數據儀表板風格
 * Overview Tab - Industrial Data Dashboard Style
 * Enhanced with data visualizations and charts
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useOrderStore, useResourceStore } from '@/store';
import { QuickGuide } from '@/components/UI/QuickGuide';

interface OverviewTabProps {
  onNavigate: (tab: 'overview' | 'orders' | 'persons' | 'gantt' | 'rush' | 'resource' | 'quality' | 'personGantt' | 'report' | 'analytics' | 'tracking') => void;
}

// 數據卡片配置
const STAT_CARDS = [
  {
    id: 'totalOrders',
    label: '總訂單數',
    icon: '📦',
    textColor: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    tab: 'orders' as const,
  },
  {
    id: 'inProgressOrders',
    label: '進行中訂單',
    icon: '⚡',
    textColor: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    tab: 'orders' as const,
  },
  {
    id: 'totalWorkOrders',
    label: '工單總數',
    icon: '🔧',
    textColor: 'text-violet-600',
    bgColor: 'bg-violet-50',
    borderColor: 'border-violet-200',
    tab: 'gantt' as const,
  },
  {
    id: 'totalPersons',
    label: '人員數量',
    icon: '👥',
    textColor: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    tab: 'persons' as const,
  },
  {
    id: 'totalMachines',
    label: '機台設備',
    icon: '⚙️',
    textColor: 'text-rose-600',
    bgColor: 'bg-rose-50',
    borderColor: 'border-rose-200',
    tab: 'overview' as const,
  },
  {
    id: 'lowStockMaterials',
    label: '低庫存警報',
    icon: '⚠️',
    textColor: 'text-red-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    tab: 'overview' as const,
  },
];

// 訂單狀態配置
const ORDER_STATUS_CONFIG = {
  '已完成': { color: 'bg-green-500', text: 'text-green-600', border: 'border-green-500/30' },
  '進行中': { color: 'bg-amber-500', text: 'text-amber-600', border: 'border-amber-500/30' },
  '已延遲': { color: 'bg-red-500', text: 'text-red-600', border: 'border-red-500/30' },
  '待處理': { color: 'bg-slate-500', text: 'text-slate-600', border: 'border-slate-500/30' },
};

export function OverviewTab({ onNavigate }: OverviewTabProps) {
  const orders = useOrderStore((state) => state.orders);
  const mainWorkOrders = useOrderStore((state) => state.mainWorkOrders);
  const subWorkOrders = useOrderStore((state) => state.subWorkOrders);
  const persons = useResourceStore((state) => state.persons);
  const machines = useResourceStore((state) => state.machines);
  const materials = useResourceStore((state) => state.materials);

  // 數據統計 - 使用 useMemo 避免重複計算
  const stats = useMemo(() => ({
    totalOrders: orders.length,
    inProgressOrders: orders.filter((o) => o.status === '進行中').length,
    totalWorkOrders: mainWorkOrders.length + subWorkOrders.length,
    totalPersons: persons.length,
    totalMachines: machines.length,
    lowStockMaterials: materials.filter((m) => m.stockQuantity <= m.safetyStock).length,
  }), [orders, mainWorkOrders, subWorkOrders, persons, machines, materials]);

  // 訂單狀態分布
  const orderStatusDistribution = useMemo(() => {
    const distribution = orders.reduce((acc, order) => {
      acc[order.status] = (acc[order.status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(distribution).map(([status, count]) => ({
      status,
      count,
      percentage: ((count / orders.length) * 100).toFixed(1),
    })).sort((a, b) => b.count - a.count);
  }, [orders]);

  // 部門人員分布
  const departmentDistribution = useMemo(() => {
    const distribution = persons.reduce((acc, person) => {
      acc[person.department] = (acc[person.department] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(distribution).map(([dept, count]) => ({
      department: dept,
      count,
      percentage: ((count / persons.length) * 100).toFixed(1),
    })).sort((a, b) => b.count - a.count);
  }, [persons]);

  // 機台狀態分布
  const machineStatusDistribution = useMemo(() => {
    const distribution = machines.reduce((acc, machine) => {
      if (machine.status === '啟用' && machine.maintenanceStatus === '正常') {
        acc.正常 += 1;
      } else if (machine.status === '停用') {
        acc.停用 += 1;
      } else {
        acc.維修中 += 1;
      }
      return acc;
    }, { 正常: 0, 停用: 0, 維修中: 0 });

    return Object.entries(distribution).map(([status, count]) => ({
      status,
      count,
      percentage: ((count / machines.length) * 100).toFixed(1),
    }));
  }, [machines]);

  // 低庫存物料列表（取前 5 名）
  const lowStockMaterials = useMemo(() => {
    return materials
      .filter((m) => m.stockQuantity <= m.safetyStock)
      .map((m) => ({
        ...m,
        shortage: m.safetyStock - m.stockQuantity,
        shortagePercent: ((m.safetyStock - m.stockQuantity) / m.safetyStock * 100).toFixed(1),
      }))
      .sort((a, b) => b.shortage - a.shortage)
      .slice(0, 5);
  }, [materials]);

  // 動畫數值狀態
  const [animatedValues, setAnimatedValues] = useState({
    totalOrders: 0,
    inProgressOrders: 0,
    totalWorkOrders: 0,
    totalPersons: 0,
    totalMachines: 0,
    lowStockMaterials: 0,
  });

  // 數值動畫效果
  useEffect(() => {
    const duration = 1500;
    const steps = 60;
    const stepDuration = duration / steps;
    const intervals: NodeJS.Timeout[] = [];

    Object.keys(stats).forEach((key) => {
      let currentStep = 0;
      const targetValue = stats[key as keyof typeof stats];
      const startValue = animatedValues[key as keyof typeof animatedValues];

      const interval = setInterval(() => {
        currentStep++;
        const progress = currentStep / steps;
        const easeProgress = 1 - Math.pow(1 - progress, 3); // easeOutCubic

        setAnimatedValues((prev) => ({
          ...prev,
          [key]: Math.round(startValue + (targetValue - startValue) * easeProgress),
        }));

        if (currentStep >= steps) {
          clearInterval(interval);
        }
      }, stepDuration);

      intervals.push(interval);
    });

    return () => {
      // 清理所有定時器
      intervals.forEach(clearInterval);
    };
  }, [stats]);

  // 處理卡片點擊
  const handleCardClick = useCallback((tab: 'overview' | 'orders' | 'persons' | 'gantt') => {
    onNavigate(tab);
  }, [onNavigate]);

  return (
    <div className="space-y-6">
      {/* 歡迎橫幅 */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-white via-[#f8fafc] to-[#f1f5f9] border border-[#e2e8f0] p-8 shadow-sm">
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-blue-500/10 to-violet-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-emerald-500/10 to-cyan-500/10 rounded-full blur-3xl"></div>

        <div className="relative z-10">
          <h1 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-violet-400 to-emerald-400 mb-3">
            製造排程系統
          </h1>
          <p className="text-lg text-[#64748b] mb-6">
            Manufacturing Scheduling System - 指揮中心
          </p>
          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-2 px-4 py-2 bg-white/50 rounded-lg border border-[#e2e8f0]">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
              <span className="text-sm text-[#475569]">系統運行中</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-white/50 rounded-lg border border-[#e2e8f0]">
              <span className="text-sm text-[#64748b]">資料更新時間：</span>
              <span className="text-sm text-[#475569] font-mono">{new Date().toLocaleString('zh-TW')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* 快速入門指南 - 新使用者（無資料）時顯示，或有資料但未關閉時顯示 */}
      <QuickGuide onNavigate={onNavigate} showForNewUser={orders.length === 0} />

      {/* 數據統計卡片網格 */}
      <div>
        <h2 className="text-xl font-bold text-[#0f172a] mb-4 flex items-center gap-2">
          <span className="text-2xl">📊</span>
          <span>數據概覽</span>
          <span className="text-sm font-normal text-[#64748b] ml-2">（點擊卡片查看詳情）</span>
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {STAT_CARDS.map((card) => {
            // 直接使用實際統計值，避免動畫問題
            const value = stats[card.id as keyof typeof stats];
            const warning = card.id === 'lowStockMaterials' && value > 0;

            // 計算次要數值或描述
            const getSubtitle = () => {
              switch (card.id) {
                case 'totalOrders':
                  return `進行中 ${stats.inProgressOrders}`;
                case 'inProgressOrders':
                  const avgProgress = orders.length > 0 && stats.inProgressOrders > 0
                    ? (orders.filter(o => o.status === '進行中').reduce((sum, o) => sum + o.progress, 0) / stats.inProgressOrders).toFixed(0)
                    : 0;
                  return `平均進度 ${avgProgress}%`;
                case 'totalWorkOrders':
                  return `主工單 ${mainWorkOrders.length}`;
                case 'totalPersons':
                  const activeCount = persons.filter(p => p.status === '啟用').length;
                  return `啟用 ${activeCount}`;
                case 'totalMachines':
                  const activeMachines = machines.filter(m => m.status === '啟用').length;
                  return `啟用 ${activeMachines}`;
                case 'lowStockMaterials':
                  return value > 0 ? '需補貨' : '正常';
                default:
                  return '';
              }
            };

            const subtitle = getSubtitle();

            return (
              <button
                key={card.id}
                onClick={() => handleCardClick(card.tab)}
                className={`
                  group relative overflow-hidden rounded-2xl p-5 border-2 transition-all duration-300
                  ${card.bgColor} ${card.borderColor}
                  hover:scale-[1.03] hover:shadow-xl hover:shadow-black/5
                  active:scale-[0.98]
                `}
              >
                {/* 裝飾背景圓形 */}
                <div className={`absolute -top-4 -right-4 w-20 h-20 rounded-full opacity-20 ${card.textColor.replace('text-', 'bg-')}`}></div>

                <div className="relative z-10 text-center">
                  <div className="flex items-center justify-center gap-3 mb-3">
                    <span className="text-3xl transform transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3">
                      {card.icon}
                    </span>
                    <span className={`text-4xl font-black ${card.textColor} tracking-tight`}>
                      {value}
                    </span>
                  </div>
                  <div className="text-sm font-bold text-[#1e293b] leading-tight mb-1">{card.label}</div>
                  {subtitle && (
                    <div className="text-xs font-medium text-[#64748b] bg-white/50 rounded-full px-2 py-0.5 inline-block mt-1">{subtitle}</div>
                  )}
                </div>

                {warning && (
                  <span className="absolute top-3 right-3 w-3 h-3 bg-red-500 rounded-full animate-ping"></span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* 數據圖表區域 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 訂單狀態分布圖表 */}
        <div className="bg-white border border-[#e2e8f0] rounded-xl p-6 shadow-sm">
          <h3 className="text-lg font-bold text-[#0f172a] mb-4 flex items-center gap-2">
            <span>📦</span>
            <span>訂單狀態分布</span>
            <span className="text-sm font-normal text-[#64748b] ml-2">({stats.totalOrders} 筆)</span>
          </h3>

          <div className="space-y-4">
            {orderStatusDistribution.map(({ status, count, percentage }) => {
              const config = ORDER_STATUS_CONFIG[status as keyof typeof ORDER_STATUS_CONFIG] || {
                color: 'bg-slate-500',
                text: 'text-slate-600',
                border: 'border-slate-500/30',
              };

              return (
                <div key={status} className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`w-2.5 h-2.5 rounded ${config.color}`}></span>
                      <span className="text-base text-[#334155]">{status}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-base font-bold text-[#0f172a]">{count}</span>
                      <span className={`text-sm ${config.text}`}>{percentage}%</span>
                    </div>
                  </div>
                  <div className="h-2.5 bg-[#f1f5f9] rounded-full overflow-hidden">
                    <div
                      className={`h-full ${config.color} transition-all duration-1000 ease-out`}
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* 總進度條 */}
          <div className="mt-4 pt-4 border-t border-[#e2e8f0]">
            <div className="flex items-center justify-between text-sm text-[#475569] mb-2">
              <span>完成率</span>
              <span className="font-medium">
                {orderStatusDistribution
                  .filter((d) => d.status === '已完成')
                  .reduce((sum, d) => sum + parseFloat(d.percentage), 0)
                  .toFixed(1)}%
              </span>
            </div>
            <div className="h-2 bg-[#f1f5f9] rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-green-500 to-emerald-400"
                style={{
                  width: `${orderStatusDistribution
                    .filter((d) => d.status === '已完成')
                    .reduce((sum, d) => sum + parseFloat(d.percentage), 0)}%`,
                }}
              ></div>
            </div>
          </div>
        </div>

        {/* 部門人員分布 */}
        <div className="bg-white border border-[#e2e8f0] rounded-xl p-6 shadow-sm">
          <h3 className="text-lg font-bold text-[#0f172a] mb-4 flex items-center gap-2">
            <span>👥</span>
            <span>部門人員分布</span>
            <span className="text-sm font-normal text-[#64748b] ml-2">({stats.totalPersons} 人)</span>
          </h3>

          <div className="space-y-4">
            {departmentDistribution.map(({ department, count, percentage }) => (
              <div key={department} className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-base text-[#334155]">{department}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-base font-bold text-[#0f172a]">{count} 人</span>
                    <span className="text-sm text-[#64748b]">{percentage}%</span>
                  </div>
                </div>
                <div className="h-2.5 bg-[#f1f5f9] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-emerald-500 to-green-400 transition-all duration-1000"
                    style={{ width: `${percentage}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 第二行圖表 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 機台狀態圓餅圖 */}
        <div className="bg-white border border-[#e2e8f0] rounded-xl p-6 shadow-sm">
          <h3 className="text-lg font-bold text-[#0f172a] mb-4 flex items-center gap-2">
            <span>⚙️</span>
            <span>機台狀態</span>
          </h3>

          <div className="flex items-center justify-center mb-4">
            <div className="relative w-36 h-36">
              <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                {machineStatusDistribution.map(({ status, percentage }, index) => {
                  const colors = {
                    正常: '#22c55e',
                    停用: '#94a3b8',
                    維修中: '#f59e0b',
                  };
                  const prevPercentage = machineStatusDistribution
                    .slice(0, index)
                    .reduce((sum, d) => sum + parseFloat(d.percentage), 0);

                  return (
                    <circle
                      key={status}
                      cx="18"
                      cy="18"
                      r="15.9"
                      fill="none"
                      stroke={colors[status as keyof typeof colors]}
                      strokeWidth="3"
                      strokeDasharray={`${parseFloat(percentage)} 100`}
                      strokeDashoffset={`-${prevPercentage}`}
                      className="transition-all duration-1000"
                    />
                  );
                })}
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className="text-3xl font-bold text-[#0f172a]">{stats.totalMachines}</div>
                  <div className="text-sm text-[#64748b]">總數</div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            {machineStatusDistribution.map(({ status, count, percentage }) => {
              const statusConfig = {
                正常: { color: 'bg-green-500', text: 'text-green-600', label: '運行中' },
                停用: { color: 'bg-slate-500', text: 'text-slate-600', label: '已停用' },
                維修中: { color: 'bg-amber-500', text: 'text-amber-600', label: '維護中' },
              };
              const config = statusConfig[status as keyof typeof statusConfig];

              return (
                <div key={status} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`w-2.5 h-2.5 rounded ${config.color}`}></span>
                    <span className="text-base text-[#334155]">{config.label}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-base font-bold text-[#0f172a]">{count}</span>
                    <span className={`text-sm ${config.text}`}>{percentage}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* 物料庫存警報 */}
        <div className="bg-white border border-[#e2e8f0] rounded-xl p-6 shadow-sm lg:col-span-2">
          <h3 className="text-lg font-bold text-[#0f172a] mb-4 flex items-center gap-2">
            <span>⚠️</span>
            <span>低庫存物料警報</span>
            <span className="text-sm font-normal text-[#64748b] ml-2">
              {lowStockMaterials.length > 0 ? `(${lowStockMaterials.length} 項需補貨)` : '(無警報)'}
            </span>
          </h3>

          {lowStockMaterials.length === 0 ? (
            <div className="text-center py-8">
              <span className="text-4xl mb-2">✅</span>
              <p className="text-[#64748b]">所有物料庫存充足</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#e2e8f0]">
                    <th className="text-left py-2 text-[#475569] font-medium">物料名稱</th>
                    <th className="text-right py-2 text-[#475569] font-medium">庫存</th>
                    <th className="text-right py-2 text-[#475569] font-medium">安全存量</th>
                    <th className="text-right py-2 text-[#475569] font-medium">缺口</th>
                    <th className="text-left py-2 text-[#475569] font-medium">狀態</th>
                  </tr>
                </thead>
                <tbody>
                  {lowStockMaterials.map((material) => (
                    <tr key={material.id} className="border-b border-[#e2e8f0]/50 last:border-0">
                      <td className="py-2 text-[#0f172a]">{material.name}</td>
                      <td className="py-2 text-right text-[#64748b]">{material.stockQuantity} {material.unit}</td>
                      <td className="py-2 text-right text-[#64748b]">{material.safetyStock} {material.unit}</td>
                      <td className="py-2 text-right text-red-600 font-medium">
                        -{material.shortage} {material.unit}
                      </td>
                      <td className="py-2">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 bg-[#f1f5f9] rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-red-500 to-rose-400"
                              style={{ width: `${material.shortagePercent}%` }}
                            ></div>
                          </div>
                          <span className="text-xs text-red-600 whitespace-nowrap">{material.shortagePercent}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* 進階統計卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* 平均完成率 */}
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3 mb-3">
            <span className="text-2xl">📈</span>
            <span className="text-sm font-medium text-green-700">平均完成率</span>
          </div>
          <div className="flex items-end gap-1">
            <span className="text-4xl font-black text-green-600">
              {orders.length > 0
                ? (orders.reduce((sum, o) => sum + o.progress, 0) / orders.length).toFixed(1)
                : '0'}
            </span>
            <span className="text-lg font-semibold text-green-500 mb-1">%</span>
          </div>
          <div className="mt-3 h-2 bg-green-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-green-500 to-emerald-400 transition-all duration-1000"
              style={{
                width: `${orders.length > 0 ? orders.reduce((sum, o) => sum + o.progress, 0) / orders.length : 0}%`,
              }}
            ></div>
          </div>
        </div>

        {/* 啟用機台率 */}
        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3 mb-3">
            <span className="text-2xl">⚙️</span>
            <span className="text-sm font-medium text-blue-700">機台啟用率</span>
          </div>
          <div className="flex items-end gap-1">
            <span className="text-4xl font-black text-blue-600">
              {machineStatusDistribution.find((m) => m.status === '正常')?.percentage || '0'}
            </span>
            <span className="text-lg font-semibold text-blue-500 mb-1">%</span>
          </div>
          <div className="mt-3 h-2 bg-blue-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-blue-500 to-cyan-400 transition-all duration-1000"
              style={{
                width: `${machineStatusDistribution.find((m) => m.status === '正常')?.percentage || 0}%`,
              }}
            ></div>
          </div>
        </div>

        {/* 活躍人員 */}
        <div className="bg-gradient-to-br from-teal-50 to-emerald-50 border border-teal-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3 mb-3">
            <span className="text-2xl">✅</span>
            <span className="text-sm font-medium text-teal-700">啟用人員</span>
          </div>
          <div className="flex items-end gap-1">
            <span className="text-4xl font-black text-teal-600">
              {persons.filter((p) => p.status === '啟用').length}
            </span>
            <span className="text-lg font-semibold text-teal-500 mb-1">/{stats.totalPersons}</span>
          </div>
          <div className="mt-3 h-2 bg-teal-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-teal-500 to-emerald-400 transition-all duration-1000"
              style={{
                width: `${persons.length > 0 ? (persons.filter((p) => p.status === '啟用').length / persons.length) * 100 : 0}%`,
              }}
            ></div>
          </div>
        </div>

        {/* 準時率 */}
        <div className="bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3 mb-3">
            <span className="text-2xl">⏰</span>
            <span className="text-sm font-medium text-violet-700">準時交貨率</span>
          </div>
          <div className="flex items-end gap-1">
            <span className="text-4xl font-black text-violet-600">
              {orders.length > 0
                ? ((orders.filter((o) => o.status === '已完成').length / orders.length) * 100).toFixed(1)
                : '0'}
            </span>
            <span className="text-lg font-semibold text-violet-500 mb-1">%</span>
          </div>
          <div className="mt-3 h-2 bg-violet-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-violet-500 to-purple-400 transition-all duration-1000"
              style={{
                width: `${orders.length > 0 ? (orders.filter((o) => o.status === '已完成').length / orders.length) * 100 : 0}%`,
              }}
            ></div>
          </div>
        </div>
      </div>

      {/* 最近活動 */}
      <div className="bg-white border border-[#e2e8f0] rounded-xl p-6 shadow-sm">
        <h3 className="text-lg font-bold text-[#0f172a] mb-4 flex items-center gap-2">
          <span>🔔</span>
          <span>最近活動</span>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start gap-3 p-4 bg-[#f8fafc] rounded-lg border-l-4 border-blue-500">
            <span className="text-xl">📦</span>
            <div className="flex-1">
              <div className="text-base font-semibold text-[#0f172a]">訂單資料載入完成</div>
              <div className="text-sm text-[#475569] mt-1">{stats.totalOrders} 筆訂單可供管理</div>
            </div>
          </div>
          <div className="flex items-start gap-3 p-4 bg-[#f8fafc] rounded-lg border-l-4 border-emerald-500">
            <span className="text-xl">👥</span>
            <div className="flex-1">
              <div className="text-base font-semibold text-[#0f172a]">人員資料就緒</div>
              <div className="text-sm text-[#475569] mt-1">
                {persons.filter((p) => p.status === '啟用').length} 位啟用中 / {persons.length} 位總數
              </div>
            </div>
          </div>
          {stats.lowStockMaterials > 0 && (
            <div className="flex items-start gap-3 p-4 bg-[#f8fafc] rounded-lg border-l-4 border-red-500">
              <span className="text-xl">⚠️</span>
              <div className="flex-1">
                <div className="text-base font-semibold text-[#0f172a]">低庫存警報</div>
                <div className="text-sm text-[#475569] mt-1">{stats.lowStockMaterials} 項物料需補貨</div>
              </div>
            </div>
          )}
          <div className="flex items-start gap-3 p-4 bg-[#f8fafc] rounded-lg border-l-4 border-violet-500">
            <span className="text-xl">📊</span>
            <div className="flex-1">
              <div className="text-base font-semibold text-[#0f172a]">排程資料就緒</div>
              <div className="text-sm text-[#475569] mt-1">{stats.totalWorkOrders} 筆工單已載入</div>
            </div>
          </div>
        </div>
      </div>

      {/* CSS 動畫 */}
      <style>{`
        @keyframes loadProgress {
          from { width: 0; }
        }
      `}</style>
    </div>
  );
}
